import React, { useState, useCallback, useEffect } from 'react';
import SearchModal from '../components/SearchModal';

export interface BitrixSearchModalProps {
  /** CSS selector for trigger element */
  triggerSelector?: string;
  /** Placeholder text for search input */
  placeholder?: string;
  /** Modal title */
  title?: string;
  /** CSS class name */
  className?: string;
  /** Whether to auto-initialize on mount */
  autoInit?: boolean;
}

/**
 * BitrixSearchModal - Self-contained search modal for Bitrix integration
 *
 * Manages its own open/close state and automatically attaches click handler
 * to the specified trigger element.
 */
const BitrixSearchModal: React.FC<BitrixSearchModalProps> = ({
  triggerSelector,
  placeholder = 'Что вы хотите найти?',
  title = 'Поиск CarolinaShop',
  className = '',
  autoInit = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Get configuration from global object or props
  const config = window.LiveSearchConfig || {};
  const finalTriggerSelector = triggerSelector || config.triggerSelector || '.site_s1 .search-input';


  const openModal = useCallback(() => {
    setIsOpen(true);
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    // Restore body scroll
    document.body.style.overflow = '';
  }, []);

  // Initialize trigger click handler
  useEffect(() => {
    if (!autoInit) return;

    const handleTriggerClick = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      openModal();
    };

    const triggerElement = document.querySelector(finalTriggerSelector);
    if (!triggerElement) {
      console.warn(`Live Search: Trigger element not found: ${finalTriggerSelector}`);
      return;
    }

    // Store original click handler
    const originalClick = triggerElement.getAttribute('onclick') ||
                         (triggerElement as any).onclick;

    // Add our click handler
    triggerElement.addEventListener('click', handleTriggerClick);

    // Preserve original handler if it exists
    if (originalClick && typeof originalClick === 'function') {
      triggerElement.addEventListener('click', originalClick as EventListener);
    }

    return () => {
      triggerElement.removeEventListener('click', handleTriggerClick);
      if (originalClick && typeof originalClick === 'function') {
        triggerElement.removeEventListener('click', originalClick as EventListener);
      }
    };
  }, [finalTriggerSelector, openModal, autoInit]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, closeModal]);

  // Handle click outside to close modal
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const modalElement = document.querySelector('.search-modal-overlay');
      if (modalElement && event.target === modalElement) {
        closeModal();
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isOpen, closeModal]);

  return (
    <SearchModal
      isOpen={isOpen}
      onClose={closeModal}
      placeholder={placeholder}
      title={title}
      className={className}
    />
  );
};

export default BitrixSearchModal;